// src/pages/club/settings/gateway/ClubPaymentGatewayPage.jsx
import "./ClubPaymentGatewayPage.scss";

import React, { useEffect, useMemo, useState } from "react";

import AppButton from "../../../../../commons/buttons/AppButton";
import AppContainer from "../../../../../commons/containers/AppContainer";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../../../commons/AppLoading";
import ClubProviderAPI from "../../api/ClubProviderAPI";
import FloatingButton from "../../../../../commons/buttons/FloatingButton";
import HallAppToast from "../../../../../commons/HallAppToast";
import ModalClubConfiguration from "../../components/ModalClubConfiguration";
import { faAdd } from "@fortawesome/free-solid-svg-icons";

const clubProviderAPI = new ClubProviderAPI();

/**
 * Fluxo:
 * 1) POST /v1/club/providers/mercado-pago/connect/start?companyId=...
 *    -> { authUrl } e redirect do browser para MP
 * 2) MP chama callback do BACKEND
 * 3) BACKEND redireciona para o FRONT:
 *    .../#/clube/pagamentos/callback?success=1 ou ?error=1
 *    (ou mp=ok|error, se você preferir padronizar)
 * 4) FRONT lê query, mostra toast, limpa URL, e faz refresh do status
 */
function ClubPaymentGatewayPage() {
  const [isLoading, setLoading] = useState(false);
  const [isConnected, setConnected] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [showModalConfig, setShowModalConfig] = useState(false);

  const query = useMemo(() => new URLSearchParams(window.location.search), []);

  const loadStatus = async () => {
    try {
      const res = await clubProviderAPI.getMercadoPagoStatus();
      const data = res?.data || res; // compatível com axios (res.data) e fetch (retorno direto)
      setConnected(!!data?.connected);
    } catch (e) {
      // sem toast pra não ficar chato
    } finally {
      setStatusLoaded(true);
    }
  };

  const consumeRedirectToast = () => {
    // Suporta os 2 formatos:
    // - novo: ?mp=ok | ?mp=error
    // - atual: ?success=1 | ?error=1
    const mp = query.get("mp");
    const success = query.get("success");
    const error = query.get("error");

    const isOk = mp === "ok" || success === "1";
    const isError = mp === "error" || error === "1";

    if (!isOk && !isError) return;

    if (isOk) HallAppToast.success("Conta do Mercado Pago conectada!");
    if (isError) HallAppToast.error("Não foi possível conectar sua conta do Mercado Pago.");

    // limpa params para não repetir toast
    query.delete("mp");
    query.delete("success");
    query.delete("error");

    const qs = query.toString();
    const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
    window.history.replaceState({}, "", newUrl);
  };

  useEffect(() => {
    consumeRedirectToast();
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      setLoading(true);

      const res = await clubProviderAPI.connectMercadoPago();
      const data = res?.data || res;
      const authUrl = data?.authUrl;

      if (!authUrl) throw new Error("authUrl vazio");

      window.location.assign(authUrl);
    } catch (e) {
      HallAppToast.genericError();
      setLoading(false);
    }
  };

  const disconnect = async () => {
    try {
      setLoading(true);

      await clubProviderAPI.disconnectMercadoPago();

      setConnected(false);
      HallAppToast.success("Conta desconectada.");
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  };

  const showLoading = isLoading || !statusLoaded;

  return (
    <>
    
    <AppContainer>
      <div className="club-gateway-page">
        <div className="section-title mb-1">Pagamentos do Clube</div>
        <div className="section-subtitle mb-3">
          Conecte sua conta para cobrar as assinaturas dos seus clientes automaticamente.
        </div>

        <div className="gateway-card" data-connected={isConnected ? "true" : "false"}>
          <div className="gateway-card__header">
            <div className="gateway-card__headerLeft">
              <div className="gateway-card__titleRow">
                <div className="gateway-card__title">Mercado Pago</div>
                <div className={`gateway-card__badge ${isConnected ? "ok" : "off"}`}>
                  {isConnected ? "CONECTADO" : "DESCONECTADO"}
                </div>
              </div>

              <div className="gateway-card__desc">
                O cliente final paga no Mercado Pago e o HallApp usa o status do pagamento para liberar os benefícios do
                clube.
              </div>
            </div>
          </div>

          {showLoading ? (
            <div className="gateway-card__loading">
              <AppLoading />
            </div>
          ) : (
            <div className="gateway-card__actions">
              {!isConnected ? (
                <>
                  <AppButton width="100%" type="button" name="conectar conta" onClick={connect} />
                  <div className="gateway-card__hint mt-2">
                    Você será redirecionado ao Mercado Pago para autorizar a conexão.
                  </div>
                </>
              ) : (
                <>
                  <AppButton width="100%" type="button" name="desconectar" onClick={disconnect} />
                  <div className="gateway-card__hint mt-2">
                    Próximo passo: criar um Clube e gerar links de assinatura para seus clientes.
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="gateway-footer">
          <AppLinkButton name="voltar" onClick={() => window.history.back()} />
        </div>
      </div>
    </AppContainer>
    
    <FloatingButton
        icon={faAdd}
        onClick={() => setShowModalConfig(true)}
      />

      {
        showModalConfig && (<ModalClubConfiguration show={showModalConfig} />)
      }

    </>

  );
}

export default ClubPaymentGatewayPage;
